import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { GraduationCap, AlertCircle, BarChart3, Clock4, ShieldCheck } from "lucide-react";

const CACHE_KEY = "accp_compliance_cache_v1";
const TTL_MS = 5 * 60 * 1000;

const GRADE_STYLES = {
  A: { bg: "#4A7C1E", fg: "#fff", ring: "#DCF5D9" },
  B: { bg: "#2E7D5A", fg: "#fff", ring: "#D9EFE2" },
  C: { bg: "#B15C00", fg: "#fff", ring: "#FFE4A0" },
  D: { bg: "#B71C1C", fg: "#fff", ring: "#FAD7D7" },
};

const SUB = [
  { key: "certification_coverage", label: "Cert Coverage",  icon: GraduationCap, path: "/certifications", hint: "% of active DSPs with all certs non-expired" },
  { key: "incident_review_latency", label: "Incident Review", icon: AlertCircle, path: "/incidents",     hint: "% of last-30d incidents reviewed within 72h" },
  { key: "overtime_ratio",         label: "OT Ratio",      icon: BarChart3,      path: "/timesheets",    hint: "% of last-30d timesheets without an OT flag" },
  { key: "clockin_compliance",     label: "Clock-in Rate", icon: Clock4,         path: "/timesheets",    hint: "% of past scheduled shifts that have a timesheet" },
];

/**
 * Audit-readiness score rendered as a pill on Dashboard.
 * Short-term cache in localStorage (5 min) so navigating around the app
 * doesn't recompute on every visit — the backend query aggregates 4 collections.
 */
export default function ComplianceScore() {
  const nav = useNavigate();
  const [data, setData] = useState(() => {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const { at, value } = JSON.parse(raw);
      return Date.now() - at < TTL_MS ? value : null;
    } catch { return null; }
  });

  useEffect(() => {
    let cancelled = false;
    api.get("/dashboard/compliance-score").then((r) => {
      if (cancelled) return;
      setData(r.data);
      try { localStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), value: r.data })); } catch {}
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!data) {
    return (
      <div className="accp-card p-6 animate-pulse" data-testid="compliance-score-loading">
        <div className="accp-label">Compliance score</div>
        <div className="h-12 w-32 bg-accent/60 rounded mt-3" />
      </div>
    );
  }

  const style = GRADE_STYLES[data.grade] || GRADE_STYLES.C;
  const components = data.components || {};

  return (
    <div className="accp-card p-6" data-testid="compliance-score">
      <div className="flex flex-wrap items-center gap-5">
        <div
          className="relative rounded-full flex items-center justify-center shrink-0"
          style={{
            width: 112, height: 112,
            background: `conic-gradient(${style.bg} ${data.score * 3.6}deg, ${style.ring} 0deg)`,
          }}
          aria-label={`Compliance score ${data.score} out of 100`}
        >
          <div className="rounded-full bg-white flex flex-col items-center justify-center" style={{ width: 92, height: 92 }}>
            <div className="text-[30px] font-semibold text-primary leading-none" style={{ fontFamily: "Outfit" }}>
              {data.score}
            </div>
            <div className="text-[10px] text-muted-foreground tracking-wider uppercase mt-0.5">/ 100</div>
          </div>
        </div>

        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2">
            <div className="accp-label">Audit readiness score</div>
            <span
              className="text-[11px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full"
              style={{ background: style.bg, color: style.fg }}
              data-testid="compliance-grade"
            >
              Grade {data.grade}
            </span>
          </div>
          <h3 className="text-2xl font-semibold text-primary mt-1" style={{ fontFamily: "Outfit" }}>
            <ShieldCheck size={20} className="inline mr-2 -mt-1" />
            Compliance snapshot
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Based on the last {data.period_days} days · four equally-weighted components.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-5">
        {SUB.map((s) => {
          const Icon = s.icon;
          const v = components[s.key] ?? 0;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => nav(s.path)}
              className="text-left rounded-xl bg-accent/40 hover:bg-accent/60 transition p-3"
              data-testid={`compliance-${s.key}`}
              title={s.hint}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{s.label}</div>
                  <div className="text-lg font-semibold text-primary mt-0.5" style={{ fontFamily: "Outfit" }}>
                    {v}<span className="text-xs text-muted-foreground">/25</span>
                  </div>
                </div>
                <Icon size={16} className="text-primary shrink-0 mt-0.5" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
